import { Injectable, isDevMode, Inject } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
import { UserSettings, DEFAULT_USER_SETTINGS } from '../models/user-settings';
import { STORAGE_PROVIDER, StorageProvider } from '../store/localStorage/local-storage.interface';

@Injectable({
  providedIn: 'root',
})
export class LocalStorageService {
  private state: UserSettings;
  private stateSubject: BehaviorSubject<UserSettings>;
  private updateSubject = new Subject<Partial<UserSettings>>();
  private isInitialized = false;

  constructor(@Inject(STORAGE_PROVIDER) private storageProvider: StorageProvider) {
    this.state = { ...DEFAULT_USER_SETTINGS };
    this.stateSubject = new BehaviorSubject<UserSettings>(this.state);

    this.updateSubject.pipe(debounceTime(300)).subscribe((partialState) => {
      this.updateState(partialState);
    });

    this.initialize();
  }

  private async initialize(): Promise<void> {
    if (this.isInitialized) return;
    try {
      this.state = await this.storageProvider.getState();
      this.stateSubject.next(this.state);
      this.isInitialized = true;
    } catch (error) {
      this.logError('Failed to initialize state', error);
      this.state = { ...DEFAULT_USER_SETTINGS };
      this.stateSubject.next(this.state);
      this.isInitialized = true;
    }
  }

  private async updateState(partialState: Partial<UserSettings>): Promise<void> {
    try {
      this.state = {
        ...this.state,
        ...partialState,
        mediaPlayer: {
          ...this.state.mediaPlayer,
          ...partialState.mediaPlayer,
        },
      };
      await this.storageProvider.setState(this.state);
      this.stateSubject.next(this.state);
    } catch (error) {
      this.logError('Failed to update state', error);
    }
  }

  dispatch(action: { type: string; payload: any }): void {
    let partialState: Partial<UserSettings> = {};
    switch (action.type) {
      case 'SET_MEDIA_PLAYER_VOLUME':
        partialState = {
          mediaPlayer: {
            ...this.state.mediaPlayer,
            volume: this.validateNumber(action.payload, DEFAULT_USER_SETTINGS.mediaPlayer.volume, 0, 1),
          },
        };
        break;
      case 'SET_MEDIA_PLAYER_PLAYBACK_SPEED':
        partialState = {
          mediaPlayer: {
            ...this.state.mediaPlayer,
            playbackSpeed: this.validateNumber(action.payload, DEFAULT_USER_SETTINGS.mediaPlayer.playbackSpeed, 0.5, 2),
          },
        };
        break;
      case 'SET_MEDIA_PLAYER_POSITION':
        partialState = {
          mediaPlayer: {
            ...this.state.mediaPlayer,
            position: action.payload
          }
        };
        break;
      default:
        this.logWarn(`Unknown action type: ${action.type}`);
        return;
    }
    this.updateSubject.next(partialState);
  }

  select<T>(selector: (state: UserSettings) => T): Observable<T> {
    this.initialize();
    return this.stateSubject.asObservable().pipe(map(selector));
  }

  selectMediaPlayerVolume(): Observable<number> {
    return this.select(state => {
      const volume = state.mediaPlayer.volume ?? DEFAULT_USER_SETTINGS.mediaPlayer.volume;
      return this.validateNumber(volume, DEFAULT_USER_SETTINGS.mediaPlayer.volume, 0, 1);
    });
  }

  selectMediaPlayerPlaybackSpeed(): Observable<number> {
    return this.select(state => {
      const playbackSpeed = state.mediaPlayer.playbackSpeed ?? DEFAULT_USER_SETTINGS.mediaPlayer.playbackSpeed;
      return this.validateNumber(playbackSpeed, DEFAULT_USER_SETTINGS.mediaPlayer.playbackSpeed, 0.5, 2);
    });
  }

  async getState(): Promise<UserSettings> {
    await this.initialize();
    return { ...this.state };
  }

  async reset(): Promise<void> {
    this.updateSubject.next({ ...DEFAULT_USER_SETTINGS });
  }

  async clear(): Promise<void> {
    try {
      await this.storageProvider.clearState();
      this.state = { ...DEFAULT_USER_SETTINGS };
      this.stateSubject.next(this.state);
      this.isInitialized = false;
    } catch (error) {
      this.logError('Failed to clear state', error);
    }
  }

  private validateNumber(value: number | null | undefined, defaultValue: number, min: number, max: number): number {
    if (typeof value !== 'number' || isNaN(value) || !isFinite(value) || value === null || value === undefined) {
      return defaultValue;
    }
    return Math.max(min, Math.min(max, value));
  }

  private logError(message: string, error: any): void {
    if (isDevMode()) {
      console.error(`[LocalStorageService] ${message}`, error);
    }
  }

  private logWarn(message: string): void {
    if (isDevMode()) {
      console.warn(`[LocalStorageService] ${message}`);
    }
  }
}